import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Square, ChevronDown, ExternalLink } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";
import { SectionHead, Reveal } from "@/components/Reveal";
import { PLATFORMS, Note } from "@/components/icons";
import { artistLinks, trackLinks } from "@/lib/musicLinks";
import { formatDate } from "@/lib/dates";

const Eq = () => (
  <span className="flex items-end gap-[3px] h-4" aria-hidden="true">
    {[0, 0.2, 0.4, 0.1].map((d, i) => <span key={i} className="eq-bar w-[3px] h-full bg-ink rounded" style={{ animationDelay: `${d}s` }} />)}
  </span>
);

const TrackCard = ({ tr, i, playing, onToggle, t, lang, artist }) => (
  <motion.article
    initial={{ opacity: 0, y: 30, rotate: i % 2 ? 2 : -2 }}
    whileInView={{ opacity: 1, y: 0, rotate: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.6, delay: (i % 4) * 0.07 }}
    className="group rounded-[26px] sticker bg-white p-3 flex flex-col"
    data-testid={`track-card-${tr.id}`}
  >
    <div className="relative aspect-square overflow-hidden rounded-[18px] border-[2.5px] border-ink">
      <img src={tr.artwork?.replace("600x600bb", "300x300bb")} srcSet={tr.artwork ? `${tr.artwork.replace("600x600bb", "300x300bb")} 300w, ${tr.artwork} 600w` : undefined} sizes="(min-width: 1024px) 280px, (min-width: 768px) 30vw, 45vw" width="300" height="300" alt={tr.title} loading="lazy" decoding="async" className={`w-full h-full object-cover transition-transform duration-700 ${playing ? "scale-110" : "group-hover:scale-105"}`} />
      {tr.preview_url && (
        <button
          onClick={() => onToggle(tr)}
          className={`absolute bottom-3 right-3 grid place-items-center w-12 h-12 rounded-full sticker-sm btn-pop-sm btn-pop ${playing ? "bg-pink text-white" : "bg-sun text-ink"}`}
          aria-label={playing ? t.music.stop : t.music.preview}
          data-testid={`track-play-${tr.id}`}
        >
          {playing ? <Square className="w-4 h-4 fill-current" /> : <Play className="w-5 h-5 fill-current ml-0.5" />}
        </button>
      )}
      {playing && <span className="absolute top-3 left-3 rounded-full bg-sun px-2.5 py-1 sticker-sm"><Eq /></span>}
    </div>
    <h3 className="mt-3 px-1 font-display font-semibold text-lg leading-tight line-clamp-2 min-h-[2.5em]">{tr.title}</h3>
    <p className="px-1 text-xs font-bold text-ink/70" data-testid={`track-date-${tr.id}`}><time dateTime={tr.release_date}>{formatDate(tr.release_date, lang)}</time></p>
    <div className="mt-3 grid grid-cols-2 gap-1.5 px-1">
      {trackLinks(tr, artist).map((l) => (
        <a key={l.k} href={l.href} target="_blank" rel="noopener noreferrer" style={{ background: l.bg }} title={l.artist ? `${l.label} – ${t.music.artistPage}` : `${l.label} – ${tr.title}`} className={`flex h-7 min-w-0 items-center justify-center rounded-full px-2 py-1 text-center text-[10px] leading-none font-extrabold text-white whitespace-nowrap border-2 border-ink transition-transform duration-200 hover:-translate-y-0.5 sm:h-auto sm:text-[11px] sm:leading-tight sm:whitespace-normal ${l.k === "deezer" ? "col-span-2 justify-self-center w-[calc((100%_-_0.375rem)_/_2)]" : "w-full"}`} data-testid={`track-link-${l.k}-${tr.id}`} data-link-kind={l.artist ? "artist" : "track"}>
          {l.label}{l.artist && <span className="hidden font-bold opacity-85 sm:inline"> · {t.music.artistPage}</span>}
        </a>
      ))}
    </div>
  </motion.article>
);

export const MusicSection = () => {
  const { t, lang } = useI18n();
  const { tracks, content } = useSite();
  const [playing, setPlaying] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const audio = useRef(null);
  // Only the releases of the current site language (unclassified ones never reach the public API).
  const list = (Array.isArray(tracks) ? tracks : []).filter((x) => x.lang === lang);
  const visible = showAll ? list : list.slice(0, 8);
  const artist = artistLinks(content, tracks);

  useEffect(() => {
    const a = new Audio();
    a.addEventListener("ended", () => setPlaying(null));
    audio.current = a;
    return () => {
      a.pause();
      audio.current = null;
    };
  }, []);

  const toggle = (tr) => {
    const a = audio.current;
    if (!a) return;
    if (playing === tr.id) {
      a.pause();
      setPlaying(null);
      return;
    }
    a.src = tr.preview_url;
    a.play().catch(() => setPlaying(null));
    setPlaying(tr.id);
  };

  return (
    <section id="musica" className="relative scroll-mt-24 overflow-hidden bg-sun py-14 sm:py-20 lg:py-32 grain" data-testid="music-section">
      <div className="absolute -right-32 -top-32 w-[420px] h-[420px] rounded-full bg-[#ffe482]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="relative grid grid-cols-1 gap-4 sm:gap-8 lg:grid-cols-12 lg:gap-10 lg:items-start">
          <div className="lg:col-span-12 lg:pr-64">
            <SectionHead eyebrow={t.music.eyebrow} title={t.music.title} sub={t.music.sub} testid="music-section-head" eyebrowClass="bg-white" />
            <Reveal delay={0.1} className="mt-5 grid grid-cols-2 gap-2 sm:mt-8 sm:flex sm:flex-wrap sm:gap-3 lg:flex-nowrap lg:gap-2" data-testid="music-platforms">
              {PLATFORMS.filter((p) => artist[p.key]).map((p) => (
                <a
                  key={p.key}
                  href={artist[p.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ background: p.bg, color: p.fg }}
                  className={`btn-pop sticker-sm btn-pop-sm inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full px-2.5 py-2 text-[13px] font-display font-semibold sm:w-auto sm:gap-2 sm:px-4 sm:py-2.5 sm:text-base lg:px-3 lg:py-2 lg:text-sm ${p.key === "deezer" ? "col-span-2 justify-self-center w-[calc((100%_-_0.5rem)_/_2)] px-2.5 sm:w-auto sm:px-4" : "w-full"}`}
                  data-testid={`platform-link-${p.key}`}
                >
                  <Note className="w-4 h-4" /> {p.name} <ExternalLink className="w-3.5 h-3.5 opacity-80" />
                </a>
              ))}
            </Reveal>
          </div>
          <div className="hidden sm:relative sm:mx-auto sm:h-48 sm:w-56 lg:absolute lg:right-0 lg:top-0 lg:h-52 lg:w-56">
            <motion.img
              src={playing ? "/brand/jump.webp" : "/brand/sit.webp"}
              key={playing ? "jump" : "sit"}
              alt={t.imgAlt.mascot}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={playing ? { scale: 1, opacity: 1, y: [0, -24, 0], rotate: [-4, 4, -4] } : { scale: 1, opacity: 1 }}
              transition={playing ? { y: { repeat: Infinity, duration: 0.55 }, rotate: { repeat: Infinity, duration: 1.1 } } : { duration: 0.4 }}
              className="absolute bottom-0 left-1/2 -translate-x-1/2 h-full object-contain"
              data-testid="music-mascot"
            />
          </div>
        </div>

        {list.length === 0 ? (
          <p className="mt-12 font-bold" data-testid="music-empty">{t.music.empty}</p>
        ) : (
          <div className="mt-7 grid grid-cols-2 gap-4 sm:mt-12 sm:gap-6 lg:mt-14 lg:grid-cols-4" data-testid="music-grid">
            {visible.map((tr, i) => <TrackCard key={tr.id} tr={tr} i={i} playing={playing === tr.id} onToggle={toggle} t={t} lang={lang} artist={artist} />)}
          </div>
        )}
        {list.length > 8 && !showAll && (
          <div className="mt-10 flex justify-center">
            <button onClick={() => setShowAll(true)} className="btn-pop sticker inline-flex items-center gap-2 rounded-full bg-ink text-white px-6 py-3 font-display font-semibold" data-testid="music-show-all">
              {t.music.all} ({list.length}) <ChevronDown className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
